import { useMemo, useState } from 'react'
import useLoadData from '../hooks/useLoadData'
import { sortSearch } from '../utils/sortSearch'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Skeleton from '@mui/material/Skeleton'
import DrinkCard from './DrinkCard'
import NavBar from './NavBar'
import Footer from './Footer'
import FilterControls from './FilterControls'
import Typography from '@mui/material/Typography'
import { Fab, Fade } from '@mui/material'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'

function ScrollTop(props) {
  const { children, window } = props
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
    target: window ? window() : undefined,
  })

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    )
    if (anchor) {
      anchor.scrollIntoView({
        block: 'center',
      })
    }
  }

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  )
}

export default function DrinkList(props) {
  const boozeBase = {
    vodka: { variant: 'outlined', key: 'VODKA' },
    gin: { variant: 'outlined', key: 'GIN' },
    tequila: { variant: 'outlined', key: 'TEQUILA' },
    whiskey: { variant: 'outlined', key: 'WHISKEY' },
    rum: { variant: 'outlined', key: 'RUM' },
    sparkling: { variant: 'outlined', key: 'SPARKLING' },
  }
  const [filterParams, setFilterParams] = useState({
    category: 'ALL',
    booze: boozeBase,
    sortOrder: 'RANDOM',
    searchTerm: '',
  })
  const { isLoading, error, status, message, data } = useLoadData('ALL')
  const searchedDrinks = useMemo(
    () => sortSearch({ ...filterParams, drinks: data }),
    [data, filterParams],
  )

  const resetFilterParams = () => {
    setFilterParams({
      category: 'ALL',
      booze: boozeBase,
      sortOrder: 'NEW',
      searchTerm: '',
    })
  }

  const handleFilterButtons = (params) => {
    setFilterParams(params)
  }

  const contentDisplay = () => {
    if (error) {
      console.error(`ERROR: Status ${status}. ${message}`)
      return (
        <Typography variant="h4" color="error">
          Error loading cocktail menu
        </Typography>
      )
    }

    if (isLoading) {
      return (
        <>
          <Skeleton variant="rounded" width={300} height={150} sx={{ m: 1 }} />
          <Skeleton variant="rounded" width={300} height={150} sx={{ m: 1 }} />
          <Skeleton variant="rounded" width={300} height={150} sx={{ m: 1 }} />
          <Skeleton variant="rounded" width={300} height={150} sx={{ m: 1 }} />
        </>
      )
    }

    if (searchedDrinks.length < 1) {
      return (
        <Typography variant="h4" color="warning">
          No Drinks Found
        </Typography>
      )
    }

    return (
      <>
        {searchedDrinks.map((drink) => (
          <Grid key={drink.id} sx={{ display: 'flex' }}>
            <DrinkCard drink={drink} />
          </Grid>
        ))}
      </>
    )
  }

  return (
    <Box className="mainContainer">
      <div id="back-to-top-anchor"></div>
      <NavBar
        filterParams={filterParams}
        resetFilterParams={resetFilterParams}
        handleFilterButtons={handleFilterButtons}
      />
      <FilterControls
        filterParams={filterParams}
        handleFilterButtons={handleFilterButtons}
        resetFilterParams={resetFilterParams}
        resultCount={isLoading || error ? undefined : searchedDrinks.length}
        collapsible
      />
      <Box className="drinkDisplay">
        <Grid
          key={'mainGrid'}
          container
          spacing={2}
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
        >
          {contentDisplay()}
        </Grid>
      </Box>
      <ScrollTop {...props}>
        <Fab size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
      <Footer />
    </Box>
  )
}
